// frontend/src/pages/superAdmin/ManageRefereeAssignments.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import api from '../../services/api';
import Swal from 'sweetalert2';

const ManageRefereeAssignments = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [referees, setReferees] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [fiscalYears, setFiscalYears] = useState([]);
  const [activeFiscalYear, setActiveFiscalYear] = useState(null);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get active fiscal year
        const fiscalYearRes = await api.get('/fiscal-years/active');
        const activeYear = fiscalYearRes.data;
        setActiveFiscalYear(activeYear);
        
        // Get all fiscal years
        const fiscalYearsRes = await api.get('/fiscal-years');
        setFiscalYears(fiscalYearsRes.data);
        
        if (activeYear) {
          setSelectedFiscalYear(activeYear.id);
          await loadAssignmentsData(activeYear.id);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง',
          confirmButtonText: 'ตกลง'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Load assignments data when fiscal year changes
  const loadAssignmentsData = async (fiscalYearId) => {
    try {
      setLoading(true);
      
      // Get referees
      const refereesRes = await api.get('/users/referees');
      setReferees(refereesRes.data);
      
      // Get OIT questions
      const questionsRes = await api.get(`/questionnaire/4/${fiscalYearId}`);
      setQuestions(questionsRes.data);
      
      // Get assignments for each question
      const newAssignments = {};
      
      for (const question of questionsRes.data) {
        const assignmentsRes = await api.get(`/referee-assignments/question/${question.id}/${fiscalYearId}`);
        newAssignments[question.id] = assignmentsRes.data;
      }
      
      setAssignments(newAssignments);
    } catch (error) {
      console.error('Error loading assignments data:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง',
        confirmButtonText: 'ตกลง'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle fiscal year change
  const handleFiscalYearChange = async (e) => {
    const yearId = e.target.value;
    setSelectedFiscalYear(yearId);
    await loadAssignmentsData(yearId);
  };

  // Assign referee to question
  const handleAssignReferee = async (questionId) => {
    try {
      const { value: refereeId } = await Swal.fire({
        title: 'เลือกกรรมการประเมิน',
        input: 'select',
        inputOptions: Object.fromEntries(
          referees.map(referee => [referee.id, referee.name])
        ),
        inputPlaceholder: 'เลือกกรรมการประเมิน',
        showCancelButton: true,
        confirmButtonText: 'กำหนด',
        cancelButtonText: 'ยกเลิก'
      });

      if (refereeId) {
        // Check if this referee is already assigned to this question
        const existingAssignment = assignments[questionId]?.find(
          (assignment) => assignment.referee_id === parseInt(refereeId)
        );

        if (existingAssignment) {
          Swal.fire({
            icon: 'warning',
            title: 'กรรมการซ้ำ',
            text: 'กรรมการท่านนี้ได้รับการกำหนดให้ประเมินคำถามนี้แล้ว',
            confirmButtonText: 'ตกลง'
          });
          return;
        }

        // Assign referee
        await api.post('/referee-assignments', {
          referee_id: refereeId,
          question_id: questionId,
          fiscal_year_id: selectedFiscalYear
        });

        // Refresh assignments for this question
        const assignmentsRes = await api.get(`/referee-assignments/question/${questionId}/${selectedFiscalYear}`);
        setAssignments(prev => ({
          ...prev,
          [questionId]: assignmentsRes.data
        }));

        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: 'กำหนดกรรมการประเมินเรียบร้อยแล้ว',
          confirmButtonText: 'ตกลง'
        });
      }
    } catch (error) {
      console.error('Error assigning referee:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถกำหนดกรรมการประเมินได้ กรุณาลองใหม่อีกครั้ง',
        confirmButtonText: 'ตกลง'
      });
    }
  };

  // Remove referee assignment
  const handleRemoveAssignment = async (assignmentId, questionId) => {
    try {
      const result = await Swal.fire({
        icon: 'warning',
        title: 'ยืนยันการยกเลิก',
        text: 'คุณต้องการยกเลิกการกำหนดกรรมการประเมินหรือไม่?',
        showCancelButton: true,
        confirmButtonText: 'ยกเลิกการกำหนด',
        cancelButtonText: 'ปิด'
      });

      if (result.isConfirmed) {
        await api.delete(`/referee-assignments/${assignmentId}`);

        // Refresh assignments for this question
        const assignmentsRes = await api.get(`/referee-assignments/question/${questionId}/${selectedFiscalYear}`);
        setAssignments(prev => ({
          ...prev,
          [questionId]: assignmentsRes.data
        }));

        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: 'ยกเลิกการกำหนดกรรมการประเมินเรียบร้อยแล้ว',
          confirmButtonText: 'ตกลง'
        });
      }
    } catch (error) {
      console.error('Error removing assignment:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถยกเลิกการกำหนดกรรมการประเมินได้ กรุณาลองใหม่อีกครั้ง',
        confirmButtonText: 'ตกลง'
      });
    }
  };

  return (
    <div className="wrapper">
      <Header user={user} title="จัดการกรรมการประเมิน" />
      <Sidebar user={user} />

      <div className="content-wrapper">
        <section className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1>จัดการกรรมการประเมิน OIT</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <Link to="/super-admin/dashboard">หน้าหลัก</Link>
                  </li>
                  <li className="breadcrumb-item active">จัดการกรรมการประเมิน</li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="content">
          <div className="container-fluid">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">กำหนดกรรมการประเมิน OIT</h3>
                <div className="card-tools">
                  <div className="input-group input-group-sm" style={{ width: '250px' }}>
                    <select
                      className="form-control"
                      value={selectedFiscalYear || ''}
                      onChange={handleFiscalYearChange}
                      disabled={loading}
                    >
                      <option value="">เลือกปีงบประมาณ</option>
                      {fiscalYears.map(year => (
                        <option key={year.id} value={year.id}>
                          ปีงบประมาณ {year.year} {year.active ? '(ปัจจุบัน)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="card-body table-responsive p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">กำลังโหลด...</span>
                    </div>
                    <p className="mt-2">กำลังโหลดข้อมูล...</p>
                  </div>
                ) : (
                  <table className="table table-hover text-nowrap">
                    <thead>
                      <tr>
                        <th style={{ width: '5%' }}>ลำดับ</th>
                        <th style={{ width: '45%' }}>คำถาม OIT</th>
                        <th style={{ width: '40%' }}>กรรมการประเมิน</th>
                        <th style={{ width: '10%' }}>จัดการ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {questions.length > 0 ? (
                        questions.map((question, index) => (
                          <tr key={question.id}>
                            <td>{index + 1}</td>
                            <td>{question.question_text}</td>
                            <td>
                              {assignments[question.id]?.length > 0 ? (
                                <ul className="list-unstyled">
                                  {assignments[question.id].map(assignment => (
                                    <li key={assignment.id} className="mb-1">
                                      <span className="badge bg-primary me-2">
                                        {assignment.referee_name}
                                      </span>
                                      <button
                                        type="button"
                                        className="btn btn-danger btn-xs"
                                        onClick={() => handleRemoveAssignment(assignment.id, question.id)}
                                      >
                                        <i className="fas fa-times"></i>
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="text-muted">ยังไม่มีกรรมการ</span>
                              )}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => handleAssignReferee(question.id)}
                              >
                                <i className="fas fa-plus"></i> เพิ่มกรรมการ
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center py-3">
                            ไม่พบข้อมูลคำถาม OIT
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default ManageRefereeAssignments;